import { useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "./axiosInstance";
import { ParseErrors } from "./ParseErrors";
import { STATUS, type ChaseAction } from "./chase-actions.constants";
import { useConfirmationModel } from "../providers/confirmation-model";
import { useRenewModel } from "../providers/renew-model";

/**
 * Runs a chase action: ask first when it carries `confirm`, then one PATCH to
 * /chase/:id (plus the action's own path), then refresh the row. Shared by the
 * row's ⋮ menu and the expanded panel so both behave identically.
 */
const useChaseAction = (
  chaseId?: number,
  onDone?: () => void,
  version?: string
) => {
  const { showModal, hideModal } = useConfirmationModel();
  const { showRenewModal } = useRenewModel();
  const [loading, setLoading] = useState(false);

  // `answer` is whatever the confirm dialog asked for, e.g. the lapse reason.
  const run = async (action: ChaseAction, answer?: string) => {
    setLoading(true);
    const select = action.confirm?.select;
    try {
      const res = await axiosInstance.patch(
        `/chase/${chaseId}${action.path ?? ""}`,
        {
          ...action.patch,
          ...(select && answer ? { [select.field]: answer } : {}),
        }
      );
      // A version update can save but leave some documents unmoved.
      if (res.data?.failed) toast.warn(res.data.message);
      else toast.success(action.success ?? "Chase updated successfully");
      onDone?.();
    } catch (error) {
      toast.error(ParseErrors(error));
    }
    hideModal();
    setLoading(false);
  };

  const runAction = (action: ChaseAction) => {
    // Renewing has its own popup — it asks for the renewal pack, and sends the
    // documents and the status change together on /chase/:id/renewed. Keyed off
    // the patch, so every Mark as Renewed button gets it (⋮ menu, auto pot,
    // payment retry) without knowing about it.
    if (action.patch?.status === STATUS.RENEWED)
      return showRenewModal({ chaseId, version, ...action.confirm, onDone });

    if (!action.confirm) return run(action);
    showModal({
      type: "confirmation",
      ...action.confirm,
      onClick: (answer) => run(action, answer),
      loading,
    });
  };

  return { runAction, loading };
};

export default useChaseAction;
